import React, { useEffect, useRef, useState } from "react";
import ResetButton from "@/components/ResetButton";
import PromptBox from "@/components/PromptBox";
import CgRenderStudio from "@/sections/CgRenderStudio";
import { Clapperboard, Info, Repeat, Pause } from "lucide-react";
import BfxToolPicker, { BfxIcon, iconBg } from "@/components/BfxToolPicker";
import CleanPlateStudio from "@/sections/CleanPlateStudio";
import { Slider } from "@/components/ui/slider";
import GenerationGrid from "@/components/GenerationGrid";
import GenerateButton from "@/components/GenerateButton";
import UploadZone from "@/components/UploadZone";
import { api, errMsg, fmtINR, fmtUSD } from "@/lib/api";
import { useAttachments } from "@/lib/useUploader";
import useDraft from "@/lib/useDraft";
import { toast } from "sonner";

const INFO = {
  "bfx-hdr": "Converts 8-bit SDR footage into HDR. You get a ZIP of per-frame EXR images at your clip's resolution, ready for grading. Up to 20 s.",
  "bfx-reframe": "Recomposes your shot to a new size — the clip is placed where you choose and the missing areas are filled in to match. Up to 60 s.",
};

const ComingSoon = ({ t }) => (
  <div className="rounded-xl border border-[#1477d2]/40 bg-[#061226] p-4" data-testid={`bfx-coming-soon-${t.id}`}>
    <span className="ax-badge !text-[#64b7f6]">Coming Soon</span>
    <p className="text-sm text-white font-semibold mt-3">{t.name}</p>
    <p className="text-xs text-zinc-400 mt-1">{t.tagline}</p>
    <div className="text-[10px] font-mono uppercase tracking-widest text-[#64b7f6]/70 mt-4">Controls</div>
    <ul className="mt-1.5 space-y-1">{t.controls.map((c) => <li key={c} className="text-xs text-zinc-300 flex gap-2"><span className="text-[#41a7f5]">•</span>{c}</li>)}</ul>
  </div>
);

const Chips = ({ items, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-1.5" data-testid={testid}>
    {items.map((r) => (
      <button key={r} type="button" onClick={() => onChange(r)} data-testid={`${testid}-${r}`}
        className={`px-2.5 py-1 rounded-lg text-[11px] font-mono border transition-colors ${value === r ? "border-[#1e97f3] bg-[#0747a1]/40 text-white" : "border-zinc-800 text-zinc-400 hover:border-[#1477d2]/60"}`}>{r}</button>
    ))}
  </div>
);
const MAX_PROMPT = 2000;
const MIN_CLIP = 3;
const FILLS = [
  { id: "loop", label: "Loop clip", hint: "Repeat the clip until it reaches 3 s", icon: Repeat },
  { id: "freeze", label: "Freeze last frame", hint: "Hold the final frame until it reaches 3 s", icon: Pause },
];

const FillChooser = ({ vsec, value, onChange }) => (
  <div className="rounded-xl border border-[#1477d2]/50 bg-[#061226] p-3 -mt-2" data-testid="bfx-fill">
    <p className="text-xs text-zinc-300">Your clip is <b className="text-white">{vsec.toFixed(1)} s</b>. Black FX needs at least {MIN_CLIP} s — choose how to fill the extra {(MIN_CLIP - vsec).toFixed(1)} s:</p>
    <div className="grid grid-cols-2 gap-2 mt-2.5">
      {FILLS.map((f) => (
        <button key={f.id} type="button" onClick={() => onChange(f.id)} data-testid={`bfx-fill-${f.id}`} aria-pressed={value === f.id}
          className={`text-left p-2.5 rounded-lg border transition-colors ${value === f.id ? "border-[#1e97f3] bg-[#0747a1]/30 bfx-glow" : "border-zinc-800 bg-[#0B0F16] hover:border-[#1477d2]/60"}`}>
          <span className="flex items-center gap-1.5 text-xs font-semibold text-white"><f.icon className="w-3.5 h-3.5 text-[#41a7f5]" />{f.label}</span>
          <span className="block text-[10px] text-zinc-400 mt-1 leading-snug">{f.hint}</span>
        </button>
      ))}
    </div>
  </div>
);

const useEstimate = (body) => {
  const [est, setEst] = useState(null);
  const seq = useRef(0);
  const key = JSON.stringify(body);
  useEffect(() => {
    if (!body) { setEst(null); return undefined; }
    const t = setTimeout(() => {
      const id = ++seq.current;
      api.post("/bfx/estimate", body).then((r) => id === seq.current && setEst(r.data)).catch(() => id === seq.current && setEst(null));
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return est;
};

const Brand = ({ rate }) => (
  <div className="bfx-brand p-4 border-b border-[#1065c0]/40" data-testid="bfx-brand">
    <div className="flex items-center gap-3">
      <div className="h-11 w-11 rounded-xl bfx-gradient-bg flex items-center justify-center shadow-[0_8px_24px_rgba(30,151,243,0.35)]"><Clapperboard className="w-5 h-5 text-white" /></div>
      <div className="flex-1">
        <div className="font-heading text-xl font-extrabold tracking-[0.18em] bfx-gradient-text">BLACK FX</div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#64b7f6]/70">VFX tools by Black Fxtudio</div>
      </div>
    </div>
    <div className="mt-3 text-[11px] font-mono text-[#64b7f6]" data-testid="bfx-rate">Live rate · 1 USD = ₹{rate ? rate.toFixed(2) : "…"}</div>
  </div>
);

const CostPanel = ({ est }) => (
  <div className="rounded-xl border border-[#1065c0]/50 bg-[#061226] p-3" data-testid="bfx-cost">
    <div className="text-[10px] font-mono uppercase tracking-widest text-[#64b7f6]/70">Estimated cost</div>
    {est ? (
      <>
        <div className="flex items-baseline gap-2 mt-1">
          <span className="text-2xl font-heading font-bold text-white" data-testid="bfx-cost-inr">{fmtINR(est.inr)}</span>
          <span className="text-xs font-mono text-zinc-400" data-testid="bfx-cost-usd">{fmtUSD(est.usd)}</span>
        </div>
        <div className="text-[11px] text-zinc-400 mt-1" data-testid="bfx-cost-breakdown">{est.breakdown}</div>
        <div className="text-[10px] font-mono text-zinc-500 mt-0.5">at ₹{est.inr_rate.toFixed(2)} per USD · published per-second price</div>
      </>
    ) : <div className="text-xs text-zinc-500 mt-1">Upload your file to see the price.</div>}
  </div>
);

const PromptField = ({ value, onChange, label, placeholder, testid, tags }) => (
  <div>
    <div className="text-xs text-zinc-300 mb-1.5">{label}</div>
    <PromptBox value={value} onChange={onChange} minRows={3} placeholder={placeholder} testid={testid} tags={tags} maxChars={MAX_PROMPT} className="ax-input bfx-input" />
  </div>
);

export default function BlackFXStudio({ projectId, items, references = [], onUploaded, onOpen, onRetry, header, onChanged, seed, models = [], generate, modelPick, onReset }) {
  const [cfg, setCfg] = useState({ tools: [], configured: true, inr_rate: 0 });
  const [toolId, setToolId] = useDraft(`ax_bfx_tool_${projectId}`, "bfx-alphagen");
  const [prompt, setPrompt] = useDraft(`ax_draft_${projectId}_bfx_${toolId}`);
  useEffect(() => { if (modelPick?.id?.startsWith("bfx-")) setToolId(modelPick.id); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelPick]);
  const [duration, setDuration] = useState(5);
  const [busy, setBusy] = useState(false);
  const [fill, setFill] = useState("loop");
  const [frame, setFrame] = useState("1080x1920");
  const [anchor, setAnchor] = useState("center");
  const media = useAttachments(projectId, 1, onUploaded, true, "bfx-media");
  useEffect(() => { api.get("/bfx/tools").then((r) => setCfg(r.data)).catch(() => {}); }, []);
  const seedRef = useRef(null);
  const applySeed = (s) => {
    const st = s.settings || {};
    const ref = references.find((r) => r.id === (s.reference_id || s.video_id || s.image_ids?.[0]));
    media.setItems(ref ? [ref] : []);
    setFill(st.fill || "loop");
    if (st.duration && s.mode === "bfx-extend") setDuration(st.duration);
    if (st.resolution) setFrame(st.resolution);
    if (st.anchor) setAnchor(st.anchor);
    setPrompt(s.prompt || "");
  };
  useEffect(() => {
    const s = seedRef.current;
    if (s && s.mode === toolId) { seedRef.current = null; applySeed(s); return; }
    media.setItems([]); setFill("loop"); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolId]);
  useEffect(() => {
    if (!seed || !seed.mode?.startsWith("bfx-")) return;
    if (seed.mode === toolId) applySeed(seed);
    else {
      try { seed.prompt ? localStorage.setItem(`ax_draft_${projectId}_bfx_${seed.mode}`, seed.prompt) : localStorage.removeItem(`ax_draft_${projectId}_bfx_${seed.mode}`); } catch { /* quota */ }
      seedRef.current = seed;
      setToolId(seed.mode);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed]);

  const tool = cfg.tools.find((t) => t.id === toolId);
  useEffect(() => { if (cfg.tools.length && !tool) setToolId("bfx-alphagen"); }, [cfg.tools.length, tool, setToolId]);
  const file = media.items[0];
  const kind = (file?.mime || "").startsWith("image/") ? "image" : "video";
  const vsec = file?.duration;
  const isExtend = toolId === "bfx-extend";
  const videoOnly = ["bfx-extend", "bfx-hdr", "bfx-reframe"].includes(toolId);
  const noPrompt = ["bfx-hdr", "bfx-reframe"].includes(toolId);
  const short = isExtend && kind === "video" && !!vsec && vsec < MIN_CLIP;
  const est = useEstimate(file?.id && tool ? { tool: toolId, kind, video_seconds: vsec, duration: isExtend ? duration : null, fill: short ? fill : null, resolution: toolId === "bfx-reframe" ? frame : null, reference_id: toolId === "bfx-hdr" ? file.id : null } : null);

  const reason = tool?.coming_soon ? "Coming Soon"
    : media.uploading ? "Waiting for upload…"
    : !file?.id ? (isExtend ? "Add a video to extend" : videoOnly ? "Add a video" : "Add a video or image")
    : videoOnly && kind === "image" ? `${tool?.name} needs a video upload`
    : kind === "video" && !vsec ? "Reading clip length…"
    : !isExtend && kind === "video" && vsec > (tool?.max_video || 60) ? `Keep clips to ${tool?.max_video || 60} s or shorter`
    : prompt.length > MAX_PROMPT ? `Prompt too long — max ${MAX_PROMPT} characters` : "";

  const go = async () => {
    if (!tool.ready) { toast.message("Coming Soon", { description: `${tool.name} will be available shortly.` }); return; }
    setBusy(true);
    try {
      await api.post("/bfx/generate", { project_id: projectId, tool: toolId, reference_id: file.id, prompt: prompt.replace(/@video_?\d+\b/g, "the source video").replace(/@image_?\d+\b/g, "the source image"), duration: isExtend ? duration : null, fill: short ? fill : null, resolution: toolId === "bfx-reframe" ? frame : null, anchor });
      toast.success(`Request sent — ${tool.name} will appear in History`);
      onChanged();
    } catch (e) { toast.error(errMsg(e, "Could not start Black FX job")); } finally { setBusy(false); }
  };

  const toolCard = (
    <div className="rounded-xl border border-[#1065c0]/50 bg-[#061226]/80 p-3 flex items-center gap-3" data-testid="bfx-tool-card">
      <span className={`h-10 w-10 rounded-lg ${iconBg(toolId, "bfx-gradient-bg text-white")} flex items-center justify-center shrink-0`}><BfxIcon id={toolId} /></span>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-white truncate" data-testid="bfx-tool-name"><span className="text-[#41a7f5]">BFX</span> {tool?.name.replace(/^BFX\s*/, "")}</div>
        <div className="text-[11px] text-zinc-500 line-clamp-2">{tool?.tagline}</div>
      </div>
      <BfxToolPicker tools={cfg.tools} value={toolId} onChange={setToolId} rate={cfg.inr_rate} />
    </div>
  );
  if (toolId === "bfx-cg-render") {
    return <CgRenderStudio projectId={projectId} models={models} items={items} onUploaded={onUploaded} onOpen={onOpen} onRetry={onRetry}
      header={header} onChanged={onChanged} generate={generate} tool={tool} brand={<Brand rate={cfg.inr_rate} />} toolCard={toolCard}
      resetButton={<ResetButton onReset={onReset} testid="cg-panel-reset" />} />;
  }
  if (toolId === "bfx-action") {
    return <CleanPlateStudio key="bfx-action" variant="action" projectId={projectId} models={models} items={items} onUploaded={onUploaded} onOpen={onOpen} onRetry={onRetry}
      header={header} onChanged={onChanged} generate={generate} tool={tool} brand={<Brand rate={cfg.inr_rate} />} toolCard={toolCard}
      resetButton={<ResetButton onReset={onReset} testid="action-panel-reset" />} />;
  }
  if (toolId === "bfx-clean-plate") {
    return <CleanPlateStudio key="bfx-clean-plate" projectId={projectId} models={models} items={items} onUploaded={onUploaded} onOpen={onOpen} onRetry={onRetry}
      header={header} onChanged={onChanged} generate={generate} tool={tool} brand={<Brand rate={cfg.inr_rate} />} toolCard={toolCard}
      resetButton={<ResetButton onReset={onReset} testid="cleanup-panel-reset" />} />;
  }

  return (
    <div className="flex-1 flex min-h-0 bfx" data-testid="bfx-section">
      <aside className="w-[400px] shrink-0 border-r border-[#1065c0]/40 bg-[#05080E]/75 backdrop-blur-xl flex flex-col min-h-0" data-testid="bfx-sidebar">
        <Brand rate={cfg.inr_rate} />
        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          <div className="flex justify-end -mb-3"><ResetButton onReset={onReset} testid="bfx-panel-reset" /></div>
          {toolCard}
          {tool?.coming_soon ? <ComingSoon t={tool} /> : (<>
          {tool && (
            <p className="text-[11px] text-zinc-500 leading-relaxed flex gap-2" data-testid="bfx-tool-info"><Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-[#41a7f5]" />
              {INFO[toolId] || (isExtend ? "Continues your clip from its last frame — motion, look and audio carry on seamlessly. Output is 1080p (16:9 or 9:16)."
                : "Cuts out the main subject of your real footage — frame-accurate, with soft edges, partial transparency, hair and smoke detail. You get two results: a white-on-black alpha matte and a cutout with a transparent background.")}
            </p>
          )}
          <UploadZone testid="bfx-upload" title={isExtend ? "Add video to extend" : videoOnly ? "Add video" : "Add video or image"} badge={isExtend ? "video ≥ 3 s" : toolId === "bfx-hdr" ? "video up to 20 s" : videoOnly ? "video up to 60 s" : "video up to 60 s · image"}
            subtitle={videoOnly ? "MP4 / MOV / MKV · H.264 or H.265" : "MP4 / MOV / MKV or PNG / JPEG / WEBP"}
            accept={videoOnly ? "video/mp4,video/quicktime,video/x-matroska" : "video/mp4,video/quicktime,video/x-matroska,image/png,image/jpeg,image/webp"}
            max={1} items={media.items} onFiles={media.add} onRemove={media.remove} />
          {short && <FillChooser vsec={vsec} value={fill} onChange={setFill} />}
          {toolId === "bfx-reframe" && (
            <div className="space-y-3" data-testid="bfx-reframe-controls">
              <div><div className="text-xs text-zinc-300 mb-1.5">Output size</div><Chips items={tool?.resolutions || []} value={frame} onChange={setFrame} testid="bfx-reframe-size" /></div>
              <div><div className="text-xs text-zinc-300 mb-1.5">Place the original</div><Chips items={tool?.anchors || []} value={anchor} onChange={setAnchor} testid="bfx-reframe-anchor" /></div>
            </div>
          )}
          {!noPrompt && <PromptField value={prompt} onChange={setPrompt} testid="bfx-prompt" tags={media.items.map((r) => ({ tag: (r.mime || "").startsWith("video/") ? "video1" : "image1", ref: r }))}
            label={isExtend ? <>What happens next <span className="text-zinc-500">(optional)</span></> : <>Notes <span className="text-zinc-500">(optional — the main subject is detected automatically)</span></>}
            placeholder={isExtend ? "e.g. the camera keeps pushing in as smoke rises" : "e.g. shot 12 — kids dancing, for comp"} />}
          {isExtend && (
            <div>
              <div className="text-xs text-zinc-300 mb-1.5">Extend by</div>
              <div className="flex items-center gap-3" data-testid="bfx-duration">
                <span className="text-[11px] font-mono text-zinc-600 w-6">2s</span>
                <Slider value={[duration]} min={2} max={20} step={1} onValueChange={([v]) => setDuration(v)} data-testid="bfx-duration-slider"
                  className="flex-1 [&_[role=slider]]:bg-[#1e97f3] [&_[role=slider]]:border-[#1e97f3] [&>span:first-child>span]:bg-gradient-to-r [&>span:first-child>span]:from-[#1065c0] [&>span:first-child>span]:to-[#41a7f5]" />
                <span className="text-[11px] font-mono text-zinc-600 w-7">20s</span>
                <span className="ax-chip h-8 min-w-[52px] justify-center font-mono text-white !border-[#1477d2]/60" data-testid="bfx-duration-value">{duration}s</span>
              </div>
            </div>
          )}
          <CostPanel est={est} />
          </>)}
        </div>
        <div className="p-4 border-t border-[#1065c0]/40">
          <GenerateButton usd={est?.usd ?? null} onClick={go} busy={busy} disabledReason={reason} testid="bfx-generate" confirm={kind === "video"} className="w-full bfx-generate" />
          {reason && <p className="text-[11px] text-zinc-500 text-center mt-2" data-testid="bfx-generate-hint">{reason}</p>}
        </div>
      </aside>
      <main className="flex-1 overflow-y-auto p-6 bg-[#070A10]/40" data-testid="bfx-history">
        {header}
        <GenerationGrid items={items} onOpen={onOpen} onRetry={onRetry} onChanged={onChanged} cols="grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3"
          emptyTitle="No Black FX results yet" emptyHint="Choose a BFX tool with Change on the left, upload your file and press Generate." />
      </main>
    </div>
  );
}
